/**
 * Administração › Usuários: números que filtram, busca, lista e o detalhe ao lado — Nexora Grid (frontend, US-1804).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 2.0.0
 *
 * ADR-0014: a equipe do integrador vê todos os usuários e escolhe a operadora; o administrador do cliente vê
 * só a própria; o suporte consulta as operadoras atribuídas. Quem pode criar vê o botão "Novo usuário".
 * Quem decide de fato é o backend (`/api/v1/usuarios`).
 * v2.0 (US-4604, Sprint 46): padrão de lista do guia (§4.1) — KPIs da página carregada (total, ativos, bloqueados,
 *   convites pendentes, desativados) que filtram, barra de filtros com pílulas e estado no endereço, lista densa
 *   (cartões no celular) e o painel de detalhe ao lado; ↑ ↓ andam, Enter abre a ficha. Mesma chamada e paginação.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import {
  BarraFiltros, CabecalhoTela, EstadoVazio, ErroCarregar, Esqueleto, FaixaKpis, ListaDetalhe, Segmentado, Selo, TabelaDensa,
  useLembrarEndereco, useParametro, type Coluna,
} from "../componentes/Tela";
import { idadeRelativa, ordenar, type Ordem } from "../componentes/regrasTela";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { AbasAdmin } from "./AbasAdmin";
import { descreverJanela, NOME_PAPEL, PERFIS, quando, situacao, type Pagina, type UsuarioAdm } from "./tipos";
import { marcaAtual } from "../marca";
import "./admin-b.css";

const POR_PAGINA = 50;
type TomSelo = "ok" | "atencao" | "suave" | "ruim";
const TOM: Record<string, TomSelo> = { Ativo: "ok", Bloqueado: "atencao", "Convite pendente": "suave", Desativado: "suave" };
const KPI_SITUACAO: Record<string, string> = { ativos: "Ativo", bloqueados: "Bloqueado", convites: "Convite pendente", desativados: "Desativado" };
const ROTULO_KPI: Record<string, string> = { ativos: "Ativos", bloqueados: "Bloqueados", convites: "Convites pendentes", desativados: "Desativados" };

/** Celular: a lista vira cartões (no desktop, só a tabela existe na página). */
function useEstreito(limite = 720): boolean {
  const consulta = `(max-width: ${limite}px)`;
  const [estreito, setEstreito] = useState(() => typeof window !== "undefined" && window.matchMedia(consulta).matches);
  useEffect(() => {
    const m = window.matchMedia(consulta);
    const mudou = () => setEstreito(m.matches);
    m.addEventListener("change", mudou);
    return () => m.removeEventListener("change", mudou);
  }, [consulta]);
  return estreito;
}

const papeisDe = (u: UsuarioAdm) => (u.perfil === "cliente" ? u.papeis.map((p) => NOME_PAPEL[p] ?? p).join(", ") : PERFIS[u.perfil]);
const operadoraDe = (u: UsuarioAdm) => u.empresa ?? (u.empresas_atribuidas.join(", ") || "todas");

function Detalhe({ u, aoFechar }: { u: UsuarioAdm; aoFechar: () => void }) {
  const s = situacao(u);
  return (
    <div className="tl-det-corpo">
      <div className="tl-det-titulo"><div style={{ flex: 1, minWidth: 0 }}><h2>{u.nome}</h2><p className="tl-mono">{u.login}</p></div>
        <button type="button" className="tl-fantasma tl-fechar" onClick={aoFechar} aria-label="Fechar o detalhe">×</button></div>
      <div className="tl-linha-acoes"><Selo tom={TOM[s.texto]}>{s.texto}</Selo>{u.em_sessao && <Selo>em sessão agora</Selo>}</div>
      <dl className="tl-kv">
        <dt>Perfil</dt><dd>{PERFIS[u.perfil]}</dd>
        {u.perfil === "cliente" && <><dt>Papéis</dt><dd>{u.papeis.map((p) => NOME_PAPEL[p] ?? p).join(", ") || "—"}</dd></>}
        <dt>Operadora</dt><dd>{operadoraDe(u)}</dd>
        <dt>E-mail</dt><dd>{u.email ?? "—"}{u.email ? ` (${u.email_confirmado ? "confirmado" : "não confirmado"})` : ""}</dd>
        <dt>Horário</dt><dd>{descreverJanela(u.janela_acesso)}</dd>
        <dt>Último acesso</dt><dd title={quando(u.ultimo_login)}>{u.ultimo_login ? idadeRelativa(u.ultimo_login) : "nunca entrou"}</dd>
        {u.bloqueado && <><dt>Bloqueado até</dt><dd>{quando(u.bloqueado_ate)}</dd></>}
        <dt>Criado em</dt><dd>{quando(u.criado_em)}</dd>
      </dl>
      <div className="tl-linha-acoes">
        <Link className="nx-botao nx-botao-principal" to={`/admin/usuarios/${u.id}`}>Abrir ficha</Link>
      </div>
    </div>
  );
}

export function Usuarios() {
  const { usuario, empresa } = useSessao();
  const navegar = useNavigate();
  useLembrarEndereco();
  const [parametros, setParametros] = useSearchParams();
  const nexora = usuario?.perfil === "integrador";
  const busca = parametros.get("busca") ?? "";
  const ativo = parametros.get("ativo") ?? "";
  const operadora = nexora ? (parametros.get("empresa") ?? "") : (empresa ?? "");
  const pagina = Number(parametros.get("pagina") ?? "0");
  const [kpi, setKpi] = useParametro("kpi");
  const [sel, setSel] = useParametro("sel");
  const [ordem, setOrdem] = useState<Ordem>({ coluna: "nome", crescente: true });
  const [painelAberto, setPainelAberto] = useState(false);
  const [resultado, setResultado] = useState<{ chave: string; dados: Pagina } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const estreito = useEstreito();
  const chave = JSON.stringify([usuario?.id, usuario?.capacidades, usuario?.funcoes_treinamento, nexora, operadora, busca, ativo, pagina]);
  const dados = resultado?.chave === chave ? resultado.dados : null;

  useEffect(() => {
    if (!usuario || (!nexora && !operadora)) return;
    const q = new URLSearchParams({ limite: String(POR_PAGINA), deslocamento: String(pagina * POR_PAGINA) });
    if (operadora) q.set("empresa", operadora);
    if (busca) q.set("busca", busca);
    if (ativo) q.set("ativo", ativo);
    setErro(null);
    const controle = new AbortController();
    const pedido = window.setTimeout(() => {
      api<Pagina>(`/v1/usuarios?${q}`, { sinal: controle.signal }).then((dados) => setResultado({ chave, dados }))
        .catch((e) => { if (!controle.signal.aborted) setErro(String(e.message ?? e)); });
    }, 250);  // espera parar de digitar
    return () => { window.clearTimeout(pedido); controle.abort(); };
  }, [usuario, nexora, operadora, busca, ativo, pagina, chave, tentativa]);

  const filtrar = (chave: string, valor: string) => {
    const novo = new URLSearchParams(parametros);
    if (chave !== "pagina") novo.delete("pagina");  // filtro novo volta à 1ª página
    if (valor && !(chave === "pagina" && valor === "0")) novo.set(chave, valor); else novo.delete(chave);
    setParametros(novo, { replace: true });
  };

  const itens = useMemo(() => dados?.itens ?? [], [dados]);
  const contagem = useMemo(() => {
    const c: Record<string, number> = { ativos: 0, bloqueados: 0, convites: 0, desativados: 0 };
    for (const u of itens) {
      const t = situacao(u).texto;
      for (const [k, v] of Object.entries(KPI_SITUACAO)) if (v === t) c[k]++;
    }
    return c;
  }, [itens]);
  const visiveis = useMemo(() => {
    const filtradas = itens.filter((u) => !kpi || situacao(u).texto === KPI_SITUACAO[kpi]);
    const valor = (u: UsuarioAdm) => ordem.coluna === "login" ? u.login : ordem.coluna === "situacao" ? situacao(u).texto
      : ordem.coluna === "acesso" ? u.ultimo_login : ordem.coluna === "operadora" ? operadoraDe(u) : u.nome;
    return ordenar(filtradas, valor, ordem.crescente);
  }, [itens, kpi, ordem]);

  if (!usuario || !pode.verUsuarios(usuario)) return <p className="nx-suave">Sem acesso à administração de usuários.</p>;
  const paginas = dados ? Math.max(1, Math.ceil(dados.total / POR_PAGINA)) : 1;
  const selecionado = visiveis.find((u) => u.id === sel) ?? visiveis[0] ?? null;
  const gerencia = pode.gerenciarUsuarios(usuario);
  const ajusta = (u: UsuarioAdm) => u.pode_gerenciar && gerencia
    && (u.perfil !== "integrador" || (usuario.funcoes_treinamento ?? []).includes("gestor_sistema"));

  const pilulas = [
    ...(kpi ? [{ chave: "kpi", texto: ROTULO_KPI[kpi] ?? kpi }] : []),
    ...(ativo ? [{ chave: "ativo", texto: ativo === "true" ? "Só ativos" : "Só desativados" }] : []),
    ...(nexora && operadora ? [{ chave: "empresa", texto: `Operadora ${operadora}` }] : []),
    ...(busca ? [{ chave: "busca", texto: `“${busca}”` }] : []),
  ];
  const tirar = (c: string) => (c === "kpi" ? setKpi("") : filtrar(c, ""));
  const limpar = () => {
    const novo = new URLSearchParams(parametros);
    for (const c of ["kpi", "ativo", "empresa", "busca", "pagina"]) novo.delete(c);
    setParametros(novo, { replace: true });
  };

  const colunas: Coluna<UsuarioAdm>[] = [
    { chave: "nome", titulo: "Nome", ordenavel: true, celula: (u) => (
      <div className="tl-ident"><div><b><Link to={`/admin/usuarios/${u.id}`}>{u.nome}</Link></b><span>{u.email ?? "sem e-mail"}</span></div></div>) },
    { chave: "login", titulo: "Login", ordenavel: true, celula: (u) => <span className="tl-mono">{u.login}</span> },
    { chave: "papeis", titulo: "Perfil e papéis", quebra: true, celula: papeisDe },
    { chave: "operadora", titulo: "Operadora", ordenavel: true, celula: operadoraDe },
    { chave: "situacao", titulo: "Situação", ordenavel: true, celula: (u) => { const s = situacao(u); return <Selo tom={TOM[s.texto]}>{s.texto}</Selo>; } },
    { chave: "acesso", titulo: "Último acesso", ordenavel: true, celula: (u) => (
      <span title={quando(u.ultimo_login)}>{u.ultimo_login ? idadeRelativa(u.ultimo_login) : <span className="nx-suave">nunca</span>}</span>) },
    { chave: "vinculos", titulo: "Acesso", celula: (u) => (ajusta(u) ? <Link to={`/admin/usuarios/${u.id}?editar=1`}>Ajustar vínculos</Link> : null) },
  ];

  return (
    <section className="tl-pagina admin-b">
      <AbasAdmin usuario={usuario} />
      <CabecalhoTela migalhas={[{ texto: NOMES.administracao }, { texto: NOMES.usuarios }]} titulo={NOMES.usuarios}
        subtitulo={operadora || (nexora ? "todas as operadoras" : undefined)}
        acoes={gerencia ? <Link className="nx-botao nx-botao-principal" to="/admin/usuarios/novo">Novo usuário</Link> : undefined} />
      {dados && (
        <FaixaKpis ativo={kpi} aoEscolher={setKpi} itens={[
          { chave: "", rotulo: "Usuários", valor: dados.total, detalhe: paginas > 1 ? `página ${pagina + 1} de ${paginas}` : "nesta consulta" },
          { chave: "ativos", rotulo: "Ativos", valor: contagem.ativos, detalhe: "podem entrar" },
          { chave: "bloqueados", rotulo: "Bloqueados", valor: contagem.bloqueados, detalhe: "por tentativas erradas" },
          { chave: "convites", rotulo: "Convites pendentes", valor: contagem.convites, detalhe: "ainda não confirmaram" },
          { chave: "desativados", rotulo: "Desativados", valor: contagem.desativados, detalhe: "sem acesso" },
        ]} />
      )}
      <BarraFiltros busca={busca} aoBuscar={(v) => filtrar("busca", v)} placeholder="Buscar por login, nome ou e-mail"
        pilulas={pilulas} aoTirar={tirar} aoLimpar={limpar}>
        {nexora && (
          <>
            <label htmlFor="operadora-usuario">Operadora</label>
            <input id="operadora-usuario" className="tl-filtro-texto" value={operadora} onChange={(e) => filtrar("empresa", e.target.value.trim().toLowerCase())}
              placeholder={`todas (inclusive a equipe ${marcaAtual().empresa})`} />
          </>
        )}
        <Segmentado rotulo="Situação" valor={ativo} aoMudar={(v) => filtrar("ativo", v)}
          opcoes={[{ valor: "", texto: "Todas" }, { valor: "true", texto: "Ativos" }, { valor: "false", texto: "Desativados" }]} />
      </BarraFiltros>
      {erro ? <ErroCarregar mensagem={`Não foi possível carregar os usuários: ${erro}`} aoTentar={() => setTentativa((t) => t + 1)} />
        : !dados ? <Esqueleto linhas={8} /> : (
          <ListaDetalhe aberto={painelAberto}
            lista={(
              <div style={{ minWidth: 0 }}>
                <TabelaDensa rotulo={NOMES.usuarios} colunas={colunas} linhas={visiveis} chave={(u) => u.id} selecionada={selecionado?.id ?? null}
                  aoSelecionar={(u) => { setSel(u.id); setPainelAberto(true); }} aoAbrir={(u) => navegar(`/admin/usuarios/${u.id}`)}
                  ordem={ordem} aoOrdenar={setOrdem}
                  cabecalho={<span><b>{visiveis.length}</b> de {dados.total} usuário(s){operadora ? ` · ${operadora}` : ""}</span>}
                  vazio={<EstadoVazio titulo="Nenhum usuário com esses filtros" texto="Tire um filtro ou cadastre a pessoa que falta."
                    acao={pilulas.length ? <button type="button" className="nx-botao" onClick={limpar}>Limpar filtros</button>
                      : gerencia ? <Link className="nx-botao" to="/admin/usuarios/novo">Cadastrar usuário</Link> : undefined} />}
                  cartao={estreito ? (u) => {
                    const s = situacao(u);
                    return (
                      <>
                        <div className="tl-linha-acoes" style={{ justifyContent: "space-between" }}>
                          <div className="tl-ident"><div><b>{u.nome}</b><span>{u.login}</span></div></div>
                          <Selo tom={TOM[s.texto]}>{s.texto}</Selo></div>
                        <span style={{ fontSize: 13 }}>{papeisDe(u)} · {operadoraDe(u)}</span>
                        <span className="nx-suave" style={{ fontSize: 12 }}>{u.ultimo_login ? `último acesso ${idadeRelativa(u.ultimo_login)}` : "nunca entrou"}</span>
                      </>
                    );
                  } : undefined} />
                {paginas > 1 && (
                  <nav className="paginacao" aria-label="Páginas">
                    <button type="button" className="nx-botao" disabled={pagina === 0} onClick={() => filtrar("pagina", String(pagina - 1))}>Anterior</button>
                    <span>Página {pagina + 1} de {paginas}</span>
                    <button type="button" className="nx-botao" disabled={pagina + 1 >= paginas} onClick={() => filtrar("pagina", String(pagina + 1))}>Próxima</button>
                  </nav>
                )}
              </div>
            )}
            detalhe={selecionado ? <Detalhe u={selecionado} aoFechar={() => setPainelAberto(false)} /> : undefined} />
        )}
    </section>
  );
}
